import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ThemeMode, ViewMode } from '../domain/types';
import {
  type BackgroundPreset,
  type BackgroundPresetId,
  type SectionBackgroundMap,
  type WorkspaceSectionId,
  BACKGROUND_STORAGE_KEY,
  CUSTOM_BACKGROUNDS_STORAGE_KEY,
  DEFAULT_SECTION_BACKGROUNDS,
  createCustomImagePreset,
  resolveBackgroundStyle,
  sanitizeCustomPresets,
  sanitizeSectionBackgrounds,
} from '../domain/backgroundConfig';
import { syncDocumentThemeColor } from './themeColor';

export interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  backgrounds: SectionBackgroundMap;
  customPresets: BackgroundPreset[];
  setSectionBackground: (section: WorkspaceSectionId, presetId: BackgroundPresetId) => void;
  setPresetForSections: (presetId: BackgroundPresetId, sections: WorkspaceSectionId[]) => void;
  setGlobalBackground: (presetId: BackgroundPresetId) => void;
  addCustomImageBackground: (name: string, imageUrl: string, opacity?: number) => void;
  removeCustomBackground: (id: string) => void;
  resetBackgrounds: () => void;
  getActiveBackgroundStyle: (view: ViewMode | string) => React.CSSProperties;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'zunelo-theme';

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme,
}) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return defaultTheme || 'light';

    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // localStorage may fail in restricted/iframe environments
    }

    if (defaultTheme) return defaultTheme;

    return 'light'; // Zunelo defaults to light theme
  });

  const [customPresets, setCustomPresets] = useState<BackgroundPreset[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(CUSTOM_BACKGROUNDS_STORAGE_KEY);
      if (raw) return sanitizeCustomPresets(JSON.parse(raw));
    } catch {
      // Ignore
    }
    return [];
  });

  const [backgrounds, setBackgroundsState] = useState<SectionBackgroundMap>(() => {
    if (typeof window === 'undefined') return { ...DEFAULT_SECTION_BACKGROUNDS };

    try {
      const raw = localStorage.getItem(BACKGROUND_STORAGE_KEY);
      if (raw) {
        return sanitizeSectionBackgrounds(JSON.parse(raw));
      }
    } catch {
      // localStorage parsing failure fallback
    }

    return { ...DEFAULT_SECTION_BACKGROUNDS };
  });

  const applyThemeToDOM = useCallback((currentTheme: ThemeMode) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', currentTheme);
    if (currentTheme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    syncDocumentThemeColor(currentTheme);
  }, []);

  useEffect(() => {
    applyThemeToDOM(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage errors
    }
  }, [theme, applyThemeToDOM]);

  useEffect(() => {
    try {
      localStorage.setItem(BACKGROUND_STORAGE_KEY, JSON.stringify(backgrounds));
    } catch {
      // Ignore storage errors
    }
  }, [backgrounds]);

  useEffect(() => {
    try {
      localStorage.setItem(
        CUSTOM_BACKGROUNDS_STORAGE_KEY,
        JSON.stringify(
          customPresets.map((p) => ({
            id: p.id,
            name: p.name,
            imageUrl: p.imageUrl,
          }))
        )
      );
    } catch {
      // Ignore storage errors
    }
  }, [customPresets]);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev: ThemeMode) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setSectionBackground = useCallback(
    (section: WorkspaceSectionId, presetId: BackgroundPresetId) => {
      setBackgroundsState((prev) => ({
        ...prev,
        [section]: presetId,
      }));
    },
    []
  );

  const setPresetForSections = useCallback(
    (presetId: BackgroundPresetId, sections: WorkspaceSectionId[]) => {
      setBackgroundsState((prev) => {
        const next = { ...prev };
        for (const section of sections) {
          next[section] = presetId;
        }
        return next;
      });
    },
    []
  );

  const setGlobalBackground = useCallback((presetId: BackgroundPresetId) => {
    setBackgroundsState({
      radar: presetId,
      jira: presetId,
      slack: presetId,
      github: presetId,
      agents: presetId,
    });
  }, []);

  const addCustomImageBackground = useCallback((name: string, imageUrl: string, opacity = 0.5) => {
    const id = `custom-bg-${Date.now()}`;
    const preset = createCustomImagePreset(id, name || 'Fondo personalizado', imageUrl, opacity);
    setCustomPresets((prev) => [...prev, preset]);
  }, []);

  const removeCustomBackground = useCallback((id: string) => {
    setCustomPresets((prev) => prev.filter((p) => p.id !== id));
    setBackgroundsState((prev) => {
      const next = { ...prev };
      for (const [sec, presetId] of Object.entries(next)) {
        if (presetId === id) {
          next[sec as WorkspaceSectionId] = 'default';
        }
      }
      return next;
    });
  }, []);

  const resetBackgrounds = useCallback(() => {
    setBackgroundsState({ ...DEFAULT_SECTION_BACKGROUNDS });
  }, []);

  const getActiveBackgroundStyle = useCallback(
    (view: ViewMode | string) => {
      return resolveBackgroundStyle(view, backgrounds, theme, customPresets);
    },
    [backgrounds, theme, customPresets]
  );

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      backgrounds,
      customPresets,
      setSectionBackground,
      setPresetForSections,
      setGlobalBackground,
      addCustomImageBackground,
      removeCustomBackground,
      resetBackgrounds,
      getActiveBackgroundStyle,
    }),
    [
      theme,
      setTheme,
      toggleTheme,
      backgrounds,
      customPresets,
      setSectionBackground,
      setPresetForSections,
      setGlobalBackground,
      addCustomImageBackground,
      removeCustomBackground,
      resetBackgrounds,
      getActiveBackgroundStyle,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
